import React, { useRef, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

// @material-ui
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
} from '@material-ui/core'
import { SwapVert } from '@material-ui/icons'

// core
import { withLang } from '_core/hocs/withLang'
import { Price } from '_core/components/Price'
import { TextInput } from '_core/components/TextInput'
import { SelectInput } from '_core/components/SelectInput'

// style
import { useCurrencyConverterStyle } from './currencyConverterStyle'

export const RawCurrencyConverterView = forwardRef((props, ref) => {
  const {
    time,
    options,
    source,
    target,
    sourceValue,
    targetValue,
    sourceValueFormat,
    targetValueFormat,
    onSourceChange,
    onTargetChange,
    onSourceValueChange,
    flipOptions,
    getLangString: l,
  } = props

  const sourceRef = useRef()
  const setCursor = useRef()
  const classes = useCurrencyConverterStyle()

  useImperativeHandle(
    ref,
    () => ({
      setCursor: delta => {
        if (typeof setCursor.current === 'function') {
          setCursor.current(delta)
          setCursor.current = null
        }
      },
    }),
    [setCursor.current]
  )

  const onValueChange = event => {
    setCursor.current = ((input, coord) => (delta = 0) => {
      const newCoord = coord + delta < 0 ? 0 : coord + delta
      input.setSelectionRange(newCoord, newCoord)
    })(event.target, event.target.selectionStart)

    onSourceValueChange(event)
  }

  return (
    <Card className={classes.root}>
      <CardContent className={classes.content}>
        <Grid container={true}>
          <Grid item={true} xs={12} sm={8} lg={12}>
            <Typography color="textSecondary" noWrap={true} gutterBottom={true}>
              <Price value={sourceValue} hasTooltip={true} currency={source} />{' '}
              {l('r._.currencyconverter.text.equation')}
            </Typography>

            <Typography noWrap={true} gutterBottom={true}>
              <Price
                value={targetValue}
                hasTooltip={true}
                currency={target}
                rootProps={{
                  variant: 'h5',
                  color: 'primary',
                  fontWeight: 'fontWeightBold',
                }}
              />
            </Typography>
          </Grid>

          <Grid item={true} xs={12} sm={4} lg={12}>
            <Typography
              variant="body2"
              gutterBottom={true}
              noWrap={true}
              align="right"
            >
              <Typography
                component="span"
                display="inline"
                variant="caption"
                className={classes.timeLabel}
              >
                {l('r._.currencyconverter.text.date')}
              </Typography>{' '}
              {moment(time).format('LLL')}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>

      <CardContent className={classes.content}>
        <Grid container={true}>
          <Grid item={true} xs={6} sm={8} lg={6}>
            <TextInput
              helperText=""
              inputRef={sourceRef}
              value={sourceValueFormat}
              onChange={onValueChange}
              // TODO: use `key` field to hadle deleting separators
              // onKeyDown={e => console.log('onKeyDown', e.key)}
            />
          </Grid>
          <Grid item={true} xs={6} sm={4} lg={6}>
            <SelectInput
              helperText=""
              options={options}
              value={source}
              onChange={onSourceChange}
            />
          </Grid>
        </Grid>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          className={classes.flip}
        >
          <IconButton size="small" onClick={flipOptions}>
            <SwapVert />
          </IconButton>
        </Box>

        <Grid container={true}>
          <Grid item={true} xs={6} sm={8} lg={6}>
            <TextInput
              value={targetValueFormat}
              readOnly={true}
              helperText=""
            />
          </Grid>
          <Grid item={true} xs={6} sm={4} lg={6}>
            <SelectInput
              helperText=""
              value={target}
              options={options}
              onChange={onTargetChange}
            />
          </Grid>
        </Grid>

        {/* <Typography
            variant="caption"
            className={classes.unmarginTop}
          >
            Отказ от обязательств
          </Typography> */}
      </CardContent>
    </Card>
  )
})

RawCurrencyConverterView.defaultProps = {}

RawCurrencyConverterView.propTypes = {
  // self props
  time: PropTypes.number,
  source: PropTypes.string,
  target: PropTypes.string,
  sourceValue: PropTypes.string,
  targetValue: PropTypes.string,
  sourceValueFormat: PropTypes.string,
  targetValueFormat: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object),
  flipOptions: PropTypes.func,
  onSourceChange: PropTypes.func,
  onTargetChange: PropTypes.func,
  onSourceValueChange: PropTypes.func,
}

export const CurrencyConverterView = withLang(RawCurrencyConverterView)
